import * as React from "react"
import cx from "classnames"

import {
  FlowInputCallback,
  FlowInputConfig,
  FlowInputTextType,
  FlowQuestionLayout,
  FlowQuestionType,
  FlowQuestionId,
} from "types"
import * as styles from "./styles.module.scss"
import Back from "../Back"
import InputCurrency from "../InputCurrency"
import InputPercentage from "../InputPercentage"
import InputPickMany from "../InputPickMany"
import InputPickOne from "../InputPickOne"
import InputText from "../InputText"
import Section from "../Section"

export interface QuestionProps {
  currentQuestionId: FlowQuestionId
  description: string
  flowKey: FlowQuestionId
  layout: FlowQuestionLayout
  inputConfig: FlowInputConfig
  maxAnswers: number
  minAnswers: number
  nextCallback: Function
  previousCallback: Function
  previousQuestionId: FlowQuestionId
  showBack?: boolean
  onSubmit: FlowInputCallback
  title: string
  type: FlowQuestionType
  userAnswer: FlowInputConfig
}

const Question: React.FC<QuestionProps> = ({
  currentQuestionId,
  description,
  flowKey,
  inputConfig,
  layout,
  maxAnswers,
  minAnswers,
  nextCallback,
  previousCallback,
  previousQuestionId,
  showBack = true,
  onSubmit,
  title,
  type,
  userAnswer,
}) => {
  let disabled = currentQuestionId !== flowKey

  return (
    <Section
      className={styles.questionWrapper}
      flowKey={flowKey}
      currentId={currentQuestionId}
      previousId={previousQuestionId}
      dataQa={"question"}
    >
      <div className={styles.pagePadding}>
        {showBack && (
          <div className={styles.back}>
            <Back previousCallback={previousCallback} />
          </div>
        )}
        <div className={styles.question}>
          <h2 className={styles.heading}>{title}</h2>
          {description && <p className={styles.description}>{description}</p>}
        </div>
        <div
          className={cx({
            [styles.input]: true,
            [styles.currency]: type === FlowQuestionType.Currency,
            [styles.date]: type === FlowQuestionType.Date,
            [styles.email]: type === FlowQuestionType.Email,
            [styles.fullDate]: type === FlowQuestionType.FullDate,
            [styles.percentage]: type === FlowQuestionType.Percentage,
          })}
        >
          {type === FlowQuestionType.Currency && (
            <InputCurrency
              disabled={disabled}
              flowKey={flowKey}
              onSubmit={onSubmit}
              userAnswer={userAnswer}
            />
          )}
          {type === FlowQuestionType.Date && (
            <InputText
              disabled={disabled}
              flowKey={flowKey}
              inputType={FlowInputTextType.Date}
              maxLength={4}
              onSubmit={onSubmit}
              pattern={inputConfig.pattern}
              placeholder={inputConfig.placeholder}
              userAnswer={userAnswer}
            />
          )}
          {type === FlowQuestionType.FullDate && (
            <InputText
              disabled={disabled}
              flowKey={flowKey}
              inputType={FlowInputTextType.FullDate}
              maxLength={10}
              nextCallback={nextCallback}
              onSubmit={onSubmit}
              pattern={inputConfig.pattern}
              placeholder={inputConfig.placeholder}
              userAnswer={userAnswer}
            />
          )}
          {type === FlowQuestionType.Percentage && (
            <InputPercentage
              disabled={disabled}
              flowKey={flowKey}
              onSubmit={onSubmit}
              userAnswer={userAnswer}
            />
          )}
          {type === FlowQuestionType.PickOne && (
            <InputPickOne
              flowKey={flowKey}
              onSubmit={onSubmit}
              layout={
                layout === FlowQuestionLayout.Grid
                  ? FlowQuestionLayout.Grid
                  : FlowQuestionLayout.List
              }
              answers={inputConfig}
              userAnswer={userAnswer}
              nextCallback={nextCallback}
            />
          )}
          {type === FlowQuestionType.PickMany && (
            <InputPickMany
              answers={inputConfig}
              flowKey={flowKey}
              maxAnswers={maxAnswers}
              minAnswers={minAnswers}
              nextCallback={nextCallback}
              onSubmit={onSubmit}
              userAnswer={userAnswer}
            />
          )}
        </div>
      </div>
    </Section>
  )
}

export default Question
